import { FC, useEffect, useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Input, Button } from '@/components/ui';
import { IndustryAutocomplete } from './IndustryAutocomplete';
import { step6Schema, type Step6Data as FormData } from '@/validations';

interface Step5SalesTargetsProps {
  initialData?: {
    salesTarget?: {
      name?: string;
      industry?: string;
      role?: string;
    };
  };
  onNext: (data: FormData) => void;
  onBack?: () => void;
  isSubmitting?: boolean;
  skipReset?: boolean;
}

export const Step5SalesTargets: FC<Step5SalesTargetsProps> = ({
  initialData,
  onNext,
  onBack,
  isSubmitting = false,
  skipReset = false,
}) => {
  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
    reset,
  } = useForm<FormData>({
    resolver: zodResolver(step6Schema),
    defaultValues: {
      name: initialData?.salesTarget?.name || '',
      industry: initialData?.salesTarget?.industry || '',
      role: initialData?.salesTarget?.role || '',
    },
  });

  // Reset form when initialData changes, but only if it's actually different
  const prevInitialDataRef = useRef(initialData);
  useEffect(() => {
    if (skipReset) return;
    const prev = prevInitialDataRef.current?.salesTarget;
    const curr = initialData?.salesTarget;
    if (
      prev?.name === curr?.name &&
      prev?.industry === curr?.industry &&
      prev?.role === curr?.role &&
      prevInitialDataRef.current
    ) return;
    prevInitialDataRef.current = initialData;
    reset({
      name: initialData?.salesTarget?.name || '',
      industry: initialData?.salesTarget?.industry || '',
      role: initialData?.salesTarget?.role || '',
    });
  }, [initialData, reset, skipReset]);

  return (
    <form onSubmit={handleSubmit(onNext)} className="space-y-6">
      {/* Header */}
      {onBack && (
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Sales Target</h2>
          <p className="text-sm text-slate-500 mt-0.5">
            Define your primary sales target segment and goals
          </p>
        </div>
      )}

      {/* Target Name */}
      <Controller
        name="name"
        control={control}
        render={({ field }) => (
          <Input
            {...field}
            type="text"
            label="Target Name"
            placeholder="e.g., Enterprise Sales"
            fullWidth
            error={errors.name?.message}
          />
        )}
      />

      {/* Industry */}
      <Controller
        name="industry"
        control={control}
        render={({ field }) => (
          <IndustryAutocomplete
            value={field.value || ''}
            onChange={(value) => setValue('industry', value)}
            error={errors.industry?.message}
            required
          />
        )}
      />

      {/* Role */}
      <Controller
        name="role"
        control={control}
        render={({ field }) => (
          <Input
            {...field}
            type="text"
            label="Target Role"
            placeholder="e.g., CTO or VP of Engineering"
            fullWidth
            error={errors.role?.message}
          />
        )}
      />

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2.5">
        <p className="text-xs text-blue-900">
          <strong>Tip:</strong> Define a specific target segment to help us personalize your outreach strategy.
        </p>
      </div>

      {/* Actions */}
      <div className="flex gap-3 pt-6 mt-6 border-t border-slate-200 justify-end">
        {onBack && (
          <Button type="button" variant="secondary" onClick={onBack}>
            Back
          </Button>
        )}
        <Button type="submit" variant="primary" isLoading={isSubmitting}>
          {isSubmitting ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </form>
  );
};
